import { useCallback, useEffect, useMemo, useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { cleanString } from "../../../utils";
import {
  useActorInfoQuery,
  useHasOnlyQueueFromActorInfo,
} from "../../../api/UsersApi";
import { useMisconductReportSummaryListQuery } from "../misconductListQueries";
import { ReportSummaryItemApi } from "../../../api/Reports";
import ReportNavigationModal from "../../ReportNavigationModal";

import Pagination from "../../../shared-components/Pagination";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { LoadedSince } from "../../../shared-components/LoadedSince";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import { genericErrorLoadingMessage } from "../../utils";
import { useSyncSelectedReportFromList } from "../../useSyncSelectedReportFromList";
import { shouldAttachInProcessErrorsOnlyQuery } from "../../inProcessErrorsOnly";
import { useAppContext } from "../../../AppProviders";
import { CacheKeys } from "../../../api/Cache";
import { LazyLoadTable } from "../../../shared-components/LazyLoadTable";
import InProcessMisconductLazyRow from "../InProcessMisconductLazyRow";
import {
  InProcessMisconductV2026RowProvider,
  InProcessMisconductV2026RowContextValue,
} from "../InProcessMisconductV2026RowContext";
import { useMyAssignedCheckTypes } from "../../../api/ActorAPI";
import { useFamaFlags } from "../../../api/FamaFlags";
import {
  checkInProcessStepPermission,
  evaluateStepLinkAccess,
  isBlueprintAssignedToActor,
  isStepAssignedToActor,
} from "../inProcessMisconductV2026Rows";

const checkPermissionsAndRedirect = (
  canInProcess: boolean,
  canNavAmendment: boolean,
  canComplete: boolean,
  canNavPendingReview: boolean,
  canClientInProgress: boolean,
  canNavReviewed: boolean,
  navigate: NavigateFunction
) => {
  const reports_uri = "/reports/";
  if (canInProcess) {
    return;
  }
  if (canComplete) {
    navigate(reports_uri + "completed", { replace: true });
    return;
  }
  if (canNavPendingReview) {
    navigate(reports_uri + "pending_review", { replace: true });
    return;
  }
  if (canNavReviewed) {
    navigate(reports_uri + "reviewed", { replace: true });
    return;
  }
  if (canClientInProgress) {
    navigate(reports_uri + "in_progress", { replace: true });
    return;
  }
  if (canNavAmendment) {
    navigate(reports_uri + "amendment", { replace: true });
    return;
  }
  navigate("/unavailable", { replace: true });
};

/**
 * In Process queue UI with ``GET /lists/misconduct/in_process``.
 * Per-row step via ``GET /workflow_runs/{uuid}/latest_step_run`` (FAILED workflow_status rows skip the fetch).
 */
export function ReportsInProcessV2026() {
  const {
    canCancelMisconduct,
    canManageReports,
    canPostScrape,
    canConfirmProfile,
    canPostRate,
    canInternalQaEdit,
    canClientQaEdit,
    canEditAmendment360,
    canInProcess,
    canNavAmendment,
    canComplete,
    canNavPendingReview,
    canClientInProgress,
    canNavReviewed,
    canResumeWorkflow,
    canViewAllReportsWithinCompany,
  } = usePermissionValidator();
  const { refreshCache } = useAppContext();
  const { v3UiInProcessActiveSteps } = useFamaFlags();

  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState<ReportSummaryItemApi>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [failedLockWorkflowRuns, setFailedLockWorkflowRuns] = useState<
    Set<string>
  >(new Set());
  const [showActiveSteps, setShowActiveSteps] = useState(false);
  const revealActiveSteps = v3UiInProcessActiveSteps && showActiveSteps;
  const navigate = useNavigate();

  // Redirect after render: react-router ignores navigate() during the first
  // render, which left users without in-process access stuck on /reports.
  useEffect(() => {
    checkPermissionsAndRedirect(
      canInProcess,
      canNavAmendment,
      canComplete,
      canNavPendingReview,
      canClientInProgress,
      canNavReviewed,
      navigate
    );
  }, [
    canInProcess,
    canNavAmendment,
    canComplete,
    canNavPendingReview,
    canClientInProgress,
    canNavReviewed,
    navigate,
  ]);
  const pageSize = 15;
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");

  const { data: actorInfo, isLoading: loadingActorInfo } =
    useActorInfoQuery(true);
  const hasOnlyQueue = useHasOnlyQueueFromActorInfo(actorInfo);
  const { data: assignedTasks, isLoading: loadingAssignedTasks } =
    useMyAssignedCheckTypes();
  const hasTaskAssignment = Boolean(
    assignedTasks?.has_task ?? actorInfo?.has_task
  );
  const assignedStepNames = useMemo(
    () => new Set(assignedTasks?.workflow_step_names ?? []),
    [assignedTasks?.workflow_step_names]
  );
  const assignedBlueprintUuids = useMemo(
    () =>
      new Set(
        (assignedTasks?.check_type_uuids ?? []).map((uuid) =>
          uuid.toLowerCase()
        )
      ),
    [assignedTasks?.check_type_uuids]
  );

  const mayViewInProcessList =
    canInProcess && !loadingActorInfo && !hasOnlyQueue;

  const errorsOnlyQuery = shouldAttachInProcessErrorsOnlyQuery(
    search,
    canResumeWorkflow
  );

  const listsQuery = useMisconductReportSummaryListQuery({
    tab: "in_process",
    individual: !canViewAllReportsWithinCompany,
    names: filter,
    page,
    pageSize,
    errorsOnly: errorsOnlyQuery,
    enabled: mayViewInProcessList,
  });

  const loadingReports = listsQuery.isLoading;
  const reportsData = listsQuery.data;
  const reportsError = listsQuery.error;

  useEffect(() => {
    if (!loadingActorInfo && hasOnlyQueue) {
      navigate("/reports/enter_queue", {
        replace: true,
        state: { stepDone: true },
      });
    }
  }, [loadingActorInfo, hasOnlyQueue, navigate]);

  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);

  useSyncSelectedReportFromList(selectedReport, setSelectedReport, reportsData);

  const handleReportClick = useCallback(
    (report: ReportSummaryItemApi) => {
      const stepPermissions = {
        canPostRate,
        canPostScrape,
        canConfirmProfile,
        canInternalQaEdit,
        canClientQaEdit,
        canNavAmendment,
        canEditAmendment360,
      };
      const selectedLink = (
        report as ReportSummaryItemApi & {
          selected_step_ui_link?: string | null;
        }
      ).selected_step_ui_link;
      const assignment = {
        hasTaskAssignment,
        assignedStepNames,
        assignedBlueprintUuids,
      };
      const canOpenSelectedStep = selectedLink
        ? evaluateStepLinkAccess(
            {
              step_name: report.step_name ?? "",
              payload: report.step_payload ?? {},
              ui_link: selectedLink,
            },
            stepPermissions,
            assignment,
            report.blueprint_uuid
          ).canOpen
        : canManageReports &&
          checkInProcessStepPermission(
            report.step_name,
            report.step_payload,
            stepPermissions
          ) &&
          isStepAssignedToActor(report.step_name, assignment) &&
          isBlueprintAssignedToActor(report.blueprint_uuid, assignment);
      if (!canOpenSelectedStep) return;
      setSelectedReport(report);
      if (report.step_name) setIsModalOpen(true);
    },
    [
      canManageReports,
      canPostRate,
      canPostScrape,
      canConfirmProfile,
      canInternalQaEdit,
      canClientQaEdit,
      canNavAmendment,
      canEditAmendment360,
      hasTaskAssignment,
      assignedStepNames,
      assignedBlueprintUuids,
    ]
  );

  const handleUnlockSuccess = useCallback((workflowRunUUID: string) => {
    setFailedLockWorkflowRuns((prev) => {
      const newSet = new Set(prev);
      newSet.delete(workflowRunUUID);
      return newSet;
    });
  }, []);

  const rowContextValue = useMemo<InProcessMisconductV2026RowContextValue>(
    () => ({
      canManageReports,
      canCancelMisconduct,
      canPostRate,
      canPostScrape,
      canConfirmProfile,
      canInternalQaEdit,
      canClientQaEdit,
      canNavAmendment,
      canEditAmendment360,
      hasTaskAssignment,
      assignedStepNames,
      assignedBlueprintUuids,
      failedLockWorkflowRuns,
      onReportClick: handleReportClick,
      onUnlockSuccess: handleUnlockSuccess,
      showActiveSteps: revealActiveSteps,
    }),
    [
      canManageReports,
      canCancelMisconduct,
      canPostRate,
      canPostScrape,
      canConfirmProfile,
      canInternalQaEdit,
      canClientQaEdit,
      canNavAmendment,
      canEditAmendment360,
      hasTaskAssignment,
      assignedStepNames,
      assignedBlueprintUuids,
      failedLockWorkflowRuns,
      handleReportClick,
      handleUnlockSuccess,
      revealActiveSteps,
    ]
  );

  function handleLockFailed(workflowRunUUID: string) {
    setFailedLockWorkflowRuns((prev) => new Set(prev).add(workflowRunUUID));
  }

  function clearCache() {
    refreshCache(CacheKeys.reports);
    void listsQuery.refetch();
  }

  if (
    loadingActorInfo ||
    (actorInfo?.has_task && loadingAssignedTasks) ||
    hasOnlyQueue
  ) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  function buildHeader() {
    const statusHeader = v3UiInProcessActiveSteps
      ? {
          text: (
            <span className="flex items-center gap-1">
              Status
              <button
                type="button"
                className="normal-case text-gray-400 hover:text-gray-600"
                aria-pressed={showActiveSteps}
                aria-label={
                  showActiveSteps
                    ? "Hide in-progress steps"
                    : "Show in-progress steps"
                }
                title={
                  showActiveSteps
                    ? "Hide in-progress steps"
                    : "Show in-progress steps"
                }
                onClick={() => setShowActiveSteps((open) => !open)}
              >
                {showActiveSteps ? (
                  <EyeIcon className="h-4 w-4" />
                ) : (
                  <EyeSlashIcon className="h-4 w-4" />
                )}
              </button>
            </span>
          ),
          customClass: "w-48 items-center gap-1",
        }
      : { text: "Status", customClass: "w-48" };
    const tableHeader = [
      "Name / Company",
      statusHeader,
      "Check Type / Behavior Kit",
      "Date Added",
      "Due Date",
      "Report ID",
      "Client Reference ID",
    ];
    if (canCancelMisconduct) {
      tableHeader.push("Cancel Report");
    }
    return tableHeader;
  }

  const showLastUpdated = Boolean(reportsData?.timestamp);

  return (
    <>
      {showLastUpdated && (
        <div className="absolute right-2 -top-[30px] z-10 flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <LoadedSince
            reportData={reportsData}
            onClear={clearCache}
            className="flex shrink-0 items-center gap-2 text-sm text-gray-600"
          />
        </div>
      )}
      <InProcessMisconductV2026RowProvider value={rowContextValue}>
        <LazyLoadTable
          parentRows={reportsData?.report_summaries}
          parentLoading={loadingReports}
          headers={buildHeader()}
          emptyMessage="No search Items"
          keyField="report_uuid"
          rowFunction={InProcessMisconductLazyRow}
        />
      </InProcessMisconductV2026RowProvider>

      {selectedReport && (
        <ReportNavigationModal
          report={selectedReport}
          isOpen={isModalOpen}
          toggleOpen={setIsModalOpen}
          onLockFailed={handleLockFailed}
          forceLocked={failedLockWorkflowRuns.has(
            selectedReport.workflow_run_uuid
          )}
        />
      )}
      {!loadingReports && reportsData && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={reportsData.total_rows}
          search={filter}
          params={errorsOnlyQuery ? { errors_only: "1" } : undefined}
        />
      )}
    </>
  );
}
